import { useState, useCallback } from "react";
import {
  createDocumentByTenant,
  attachDocumentbyTenant,
  assignAgentToDocument,
} from "../../../../../services/document";
import { createFile, getFileUuidFromCreateResponse } from "../../../../../services/file";

const DEFAULT_TENANT_ID = "58145bef-4f71-468a-bded-1823d2a08f58";

const normalizeContentSource = (contentSource) => {
  if (!contentSource) return "";
  if (typeof contentSource === "string") return contentSource;
  if (typeof contentSource.toText === "function") {
    try {
      return contentSource.toText() || "";
    } catch {
      return "";
    }
  }
  return "";
};

const buildFileFromContent = async (fileName, fileType, content, editorState) => {
  const trimmedName = (fileName || "Document").trim() || "Document";
  const safeName = trimmedName.replace(/\s+/g, "_");
  const type = fileType?.value || "txt";
  const mimeType = fileType?.mimeType || "text/plain";

  let fileBlob;
  let fullFileName;

  switch (type) {
    case "txt":
    case "md": {
      fileBlob = new Blob([content], { type: mimeType });
      fullFileName = `${safeName}.${type}`;
      break;
    }
    case "html": {
      let htmlContent;
      if (editorState && typeof editorState.toHTML === "function") {
        htmlContent = `<!DOCTYPE html><html><head><title>${safeName}</title></head><body>${editorState.toHTML()}</body></html>`;
      } else {
        htmlContent = `<!DOCTYPE html><html><head><title>${safeName}</title></head><body><pre>${content}</pre></body></html>`;
      }
      fileBlob = new Blob([htmlContent], { type: mimeType });
      fullFileName = `${safeName}.html`;
      break;
    }
    case "pdf": {
      const { jsPDF } = await import("jspdf");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      pdf.setFontSize(18);
      pdf.setTextColor(0, 0, 0);
      pdf.text(trimmedName, 20, 20);

      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.5);
      pdf.line(20, 24, 190, 24);

      pdf.setFontSize(12);
      const paragraphs = content.split(/\r?\n/);
      let yPosition = 30;
      const lineHeight = 7;
      const pageWidth = 170;

      paragraphs.forEach((paragraph) => {
        if (paragraph.trim() === "") {
          yPosition += lineHeight / 2;
          return;
        }
        if (yPosition > 270) {
          pdf.addPage();
          yPosition = 20;
        }
        const lines = pdf.splitTextToSize(paragraph, pageWidth);
        pdf.text(lines, 20, yPosition);
        yPosition += lines.length * lineHeight + 2;
      });

      fileBlob = pdf.output("blob");
      fullFileName = `${safeName}.pdf`;
      break;
    }
    case "docx": {
      const { Document, Packer, Paragraph, TextRun } = await import("docx");

      const paragraphs = content
        .split(/\r?\n/)
        .filter((line) => line.trim() !== "");

      const docxParagraphs = paragraphs.map(
        (paragraph) =>
          new Paragraph({
            children: [new TextRun(paragraph)],
          })
      );

      const doc = new Document({
        sections: [
          {
            properties: {},
            children: docxParagraphs,
          },
        ],
      });

      fileBlob = await Packer.toBlob(doc);
      fullFileName = `${safeName}.docx`;
      break;
    }
    default: {
      fileBlob = new Blob([content], { type: "text/plain" });
      fullFileName = `${safeName}.txt`;
    }
  }

  const file = new File([fileBlob], fullFileName, { type: mimeType });
  return file;
};

const uploadToVaultFolder = async (file, destination, context) => {
  const { vaultUuid, folderUuid, vaultName, folderName } = destination;
  const { userId, tenantId, agentUuid } = context;

  // Debug: verificar contexto de guardado y agentUuid
  // eslint-disable-next-line no-console
  console.log("useSaveDocumentToDrive :: uploadToVaultFolder context", {
    vaultUuid,
    folderUuid,
    vaultName,
    folderName,
    userId,
    tenantId,
    agentUuid,
  });

  if (!vaultUuid) {
    throw new Error("Vault UUID is required to save document.");
  }

  const createDocumentBody = {
    name: file.name,
    vaultUuid,
    folderUuid: folderUuid || null,
    type: file.type || "application/octet-stream",
    url: "",
    metadata: {
      source: "document_panel",
      version: "1.0",
      region: "us-east-1",
      confidential: true,
    },
  };

  const createDocumentResponse = await createDocumentByTenant(createDocumentBody);
  const { status_code, body } = createDocumentResponse.data || {};

  if (status_code < 200 || status_code >= 300) {
    throw new Error("Failed to create document entry");
  }

  const documentUuid = body?.document?.document_uuid;
  const versionUuid = body?.version?.doc_version_uuid;

  if (!documentUuid || !versionUuid) {
    throw new Error("Invalid document creation response");
  }

  if (agentUuid) {
    if (!userId) {
      throw new Error("User ID is required to assign document to agent.");
    }
    try {
      // eslint-disable-next-line no-console
      console.log("useSaveDocumentToDrive :: assigning document to agent", {
        agentUuid,
        userId,
        documentUuid,
      });
      const assignBody = {
        document_uuid: documentUuid,
        agent_uuid: agentUuid,
        expires_at: "2026-12-31 23:59:59",
        owner: userId,
        created_by: userId,
        granted_by: userId,
      };
      await assignAgentToDocument(assignBody);
    } catch (error) {
      // Soft-fail on agent assignment
      // eslint-disable-next-line no-console
      console.warn("Agent assignment failed:", error);
    }
  }

  const createFileBody = {
    name: file.name,
    folder_id: folderUuid || "-",
    tenant_id: tenantId || DEFAULT_TENANT_ID,
    user_id: userId,
    file_type: file.type || "application/octet-stream",
    status: "active",
    vault_uuid: vaultUuid,
    document_uuid: documentUuid,
    version_uuid: versionUuid,
  };

  const createFileResponse = await createFile(createFileBody);
  const fileData = createFileResponse.data?.body || createFileResponse.data || {};

  const uploadUrl = fileData.uploadUrl;
  const fileUuid = getFileUuidFromCreateResponse(fileData);

  if (!uploadUrl || !fileUuid) {
    throw new Error("No upload URL or file_uuid received");
  }

  const uploadResponse = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type || "application/octet-stream",
    },
  });

  if (!uploadResponse.ok) {
    throw new Error(`Upload failed: ${uploadResponse.status}`);
  }

  await attachDocumentbyTenant({
    file_uuid: fileUuid,
    document_uuid: documentUuid,
  });

  return {
    vaultUuid,
    vaultName,
    folderName: folderName || "Root",
    documentUuid,
  };
};

export const useSaveDocumentToDrive = ({
  getContent,
  getTitle,
  getAgentInfo,
  getAuthContext,
  onSuccess,
  onError,
} = {}) => {
  const [isSaving, setIsSaving] = useState(false);

  const saveToVault = useCallback(
    async (fileName, fileType, destinations = []) => {
      if (!destinations || destinations.length === 0) {
        return;
      }

      setIsSaving(true);
      try {
        const rawContentSource = getContent?.();
        const content = normalizeContentSource(rawContentSource);

        if (!content || !content.trim()) {
          throw new Error("Document is empty");
        }

        const { userId, tenantId } = getAuthContext?.() || {};
        const { agentUuid } = getAgentInfo?.() || {};

        const effectiveTitle =
          fileName && fileName.trim()
            ? fileName.trim()
            : getTitle?.() || "Document";

        const file = await buildFileFromContent(
          effectiveTitle,
          fileType,
          content,
          rawContentSource
        );

        const context = {
          userId,
          tenantId: tenantId || DEFAULT_TENANT_ID,
          agentUuid,
        };

        let successCount = 0;
        let firstResult = null;
        const errors = [];

        for (const destination of destinations) {
          try {
            const result = await uploadToVaultFolder(file, destination, context);
            successCount += 1;
            if (!firstResult) {
              firstResult = {
                vaultUuid: result.vaultUuid,
                documentUuid: result.documentUuid,
                vaultName: result.vaultName,
                folderName: result.folderName,
              };
            }
          } catch (error) {
            errors.push(
              `${destination.vaultName}/${destination.folderName || "Root"}: ${
                error.message || "Unknown error"
              }`
            );
          }
        }

        if (successCount > 0) {
          const message =
            successCount === destinations.length
              ? `Document successfully saved to ${successCount} location${
                  successCount > 1 ? "s" : ""
                }!`
              : `Document saved to ${successCount} of ${destinations.length} locations. Some uploads failed.`;

          onSuccess?.(message);
          return {
            ...firstResult,
            fileName: effectiveTitle,
          };
        } else {
          throw new Error(
            `Failed to save to any location:\n${errors.join("\n")}`
          );
        }
      } catch (error) {
        onError?.(error);
      } finally {
        setIsSaving(false);
      }
    },
    [getContent, getTitle, getAgentInfo, getAuthContext, onSuccess, onError]
  );

  return {
    saveToVault,
    isSaving,
  };
};

